import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import sctLogo from '../assets/SCT.png';

interface PreloaderProps {
    onLoadingComplete?: () => void;
}

const SignalLines = ({ side, progress }: { side: 'left' | 'right', progress: number }) => {
    const isLeft = side === 'left';
    
    return (
        <div className={`absolute top-1/2 -translate-y-1/2 ${isLeft ? 'right-[90%]' : 'left-[90%]'} w-48 h-32 hidden sm:block opacity-90 pointer-events-none`}>
            <svg 
                viewBox="0 0 200 100" 
                className={`w-full h-full ${isLeft ? '' : 'transform scale-x-[-1]'}`} 
                fill="none" 
            >
                {/* Glow Filter */}
                <defs>
                    <filter id={`glow-${side}`} x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Base Paths (faint) */}
                <path d="M 0 25 H 70 L 85 10 H 195" stroke="#1e3a8a" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
                <path d="M 20 45 H 120 L 135 35 H 195" stroke="#1e3a8a" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
                <path d="M 0 65 H 90 L 105 75 H 195" stroke="#1e3a8a" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
                <path d="M 40 85 H 140 L 150 90 H 195" stroke="#1e3a8a" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
                
                {/* Animated Paths (Energy Pulses feeding the logo) */}
                <motion.path
                    d="M 0 25 H 70 L 85 10 H 195"
                    stroke="#60a5fa"
                    strokeWidth="2"
                    strokeLinecap="round"
                    filter={`url(#glow-${side})`}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: [0, 1, 1], opacity: [0, 1, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "linear", times: [0, 0.7, 1] }}
                />
                
                <motion.path
                    d="M 20 45 H 120 L 135 35 H 195"
                    stroke="#60a5fa"
                    strokeWidth="2"
                    strokeLinecap="round"
                    filter={`url(#glow-${side})`}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: [0, 1, 1], opacity: [0, 1, 0] }}
                    transition={{ duration: 1.2, repeat: Infinity, ease: "linear", delay: 0.3, times: [0, 0.7, 1] }}
                />

                <motion.path
                    d="M 0 65 H 90 L 105 75 H 195"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    filter={`url(#glow-${side})`}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: [0, 1, 1], opacity: [0, 1, 0] }}
                    transition={{ duration: 1.7, repeat: Infinity, ease: "linear", delay: 0.6, times: [0, 0.7, 1] }}
                />

                <motion.path
                    d="M 40 85 H 140 L 150 90 H 195"
                    stroke="#3b82f6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    filter={`url(#glow-${side})`}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: [0, 1, 1], opacity: [0, 1, 0] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: "linear", delay: 0.1, times: [0, 0.7, 1] }}
                />

                {/* Connection Nodes at the Logo Ring edge */}
                <circle cx="195" cy="10" r="3" fill="#60a5fa" filter={`url(#glow-${side})`} className="animate-pulse" />
                <circle cx="195" cy="35" r="3" fill="#60a5fa" filter={`url(#glow-${side})`} className="animate-pulse" />
                <circle cx="195" cy="75" r="3" fill="#3b82f6" filter={`url(#glow-${side})`} className="animate-pulse" />
                <circle cx="195" cy="90" r="3" fill="#3b82f6" filter={`url(#glow-${side})`} className="animate-pulse" />
            </svg>
        </div>
    );
};

const Preloader: React.FC<PreloaderProps> = ({ onLoadingComplete }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setProgress(prev => {
                const newProgress = prev + 1;
                if (newProgress >= 100) {
                    clearInterval(timer);
                    setTimeout(() => {
                        setIsLoading(false);
                        onLoadingComplete?.();
                    }, 600);
                    return 100;
                }
                return newProgress;
            });
        }, 30); // Speed of loading

        return () => clearInterval(timer);
    }, [onLoadingComplete]);

    // Circular progress math
    const circleRadius = 100;
    const circumference = 2 * Math.PI * circleRadius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <AnimatePresence>
            {isLoading && (
                <motion.div
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.5 } }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-[#050B14]"
                >
                    <div className="relative flex flex-col items-center">
                        
                        {/* Central Power Core / Logo Area */}
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="relative flex items-center justify-center"
                        >
                            {/* Energy signals feeding into the center */}
                            <SignalLines side="left" progress={progress} />
                            <SignalLines side="right" progress={progress} />

                            <div className="relative w-64 h-64 flex items-center justify-center">
                                {/* Dim Background Ring */}
                                <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                                    <circle
                                        cx="128"
                                        cy="128"
                                        r={circleRadius}
                                        stroke="#1e3a8a"
                                        strokeWidth="4"
                                        fill="none"
                                        className="opacity-30"
                                    />
                                    {/* Active Charging Ring */}
                                    <circle
                                        cx="128"
                                        cy="128"
                                        r={circleRadius}
                                        stroke="#60a5fa"
                                        strokeWidth="6"
                                        fill="none"
                                        strokeLinecap="round"
                                        style={{
                                            strokeDasharray: circumference,
                                            strokeDashoffset: strokeDashoffset,
                                            transition: 'stroke-dashoffset 0.1s ease-out'
                                        }}
                                        className="drop-shadow-[0_0_15px_rgba(96,165,250,0.8)]"
                                    />
                                </svg>

                                {/* Rotating Accents on the ring */}
                                <motion.div
                                    className="absolute inset-0"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                                >
                                    <div className="w-full h-full rounded-full border border-blue-400/10 relative">
                                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-[0_0_15px_rgba(255,255,255,1)]" />
                                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-blue-400 rounded-full shadow-[0_0_15px_rgba(96,165,250,1)]" />
                                    </div>
                                </motion.div>

                                {/* Logo container - Dim base layer */}
                                <div className="absolute inset-0 flex items-center justify-center p-12">
                                    <img
                                        src={sctLogo}
                                        alt="SCT Logo Base"
                                        className="w-full h-full object-contain opacity-20 grayscale"
                                    />
                                </div>

                                {/* Logo container - Bright 'Charged' layer that fills up */}
                                <div className="absolute inset-0 flex items-center justify-center p-12">
                                    <div 
                                        className="w-full h-full relative"
                                        style={{
                                            clipPath: `inset(${100 - progress}% 0 0 0)`,
                                            transition: 'clip-path 0.1s ease-out'
                                        }}
                                    >
                                        <img
                                            src={sctLogo}
                                            alt="SCT Logo Charged"
                                            className="w-full h-full object-contain drop-shadow-[0_0_20px_rgba(59,130,246,0.6)]"
                                        />
                                        <div className="absolute inset-0 bg-blue-500/20 mix-blend-screen rounded-full blur-xl" />
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Progress Status */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="mt-12 w-[280px] sm:w-[400px]"
                        >
                            <div className="text-center flex flex-col gap-2">
                                <motion.span
                                    className="text-xs sm:text-sm font-bold tracking-[0.3em] text-white uppercase"
                                    animate={{ opacity: [1, 0.7] }}
                                    transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse" }}
                                >
                                    Initializing Security
                                </motion.span>
                                
                                <div className="flex items-center justify-center gap-4 mt-2">
                                    <span className="h-[1px] w-8 bg-blue-500/50"></span>
                                    <span className="text-lg sm:text-xl font-bold tracking-widest text-blue-400 drop-shadow-[0_0_10px_rgba(96,165,250,0.5)]">
                                        {progress}%
                                    </span>
                                    <span className="h-[1px] w-8 bg-blue-500/50"></span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Preloader;
